// Permission card shown while controller.pendingPermissionCard is non-null
// (Tier 1.c § 4.4). The executor (CopilotController._requestControlPermission)
// pushes a `{ taskHint, resolve }` descriptor onto pendingPermissionCard and
// awaits the Promise; this component renders the inline UI that lets the user
// pick Allow (resolve("granted")) or Just-guide-me (resolve("denied")).
//
// Sticky-Allow flips controlPermissionRemembered ONLY on the Allow path. On
// the Deny path the sticky checkbox is intentionally ignored — per spec § 4.4
// and v1 prototype safety, "deny and remember" would permanently lock the
// user out of Drive mode for the session with no UI to undo it. Deny stays
// per-prompt.
//
// The card renders inside Thread.jsx's composer-layer (above the composer)
// so it doesn't get hidden by the floating input area. While visible it
// publishes its height as --copilot-permission-h so the viewport reserves
// padding-bottom and the last thread message stays in view. After a
// decision it pushes a system breadcrumb ("✓ Control granted" /
// "✕ Just guiding") into the thread so the decision is preserved in
// scrollback.

import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { MousePointerClick } from "lucide-react";

const BREADCRUMB = {
  granted: "✓ Control granted",
  denied:  "✕ Just guiding — you'll click",
};

export const PermissionCard = observer(function PermissionCard({ controller }) {
  const card = controller.pendingPermissionCard;
  const [sticky, setSticky] = useState(false);
  const rootRef = useRef(null);

  // Publish height as --copilot-permission-h on the closest .copilot-root so
  // the thread viewport (and feather gradient) reserve space above the
  // composer for this card. On unmount, clear the var so the viewport
  // reclaims the padding.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const panel = el.closest(".copilot-root");
    if (!panel) return undefined;
    let lastH = -1;
    let rafId = 0;
    const update = () => {
      const h = el.offsetHeight;
      if (h === lastH) return;
      lastH = h;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        panel.style.setProperty("--copilot-permission-h", `${h}px`);
        rafId = 0;
      });
    };
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => {
      ro.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      panel.style.setProperty("--copilot-permission-h", "0px");
    };
  }, [card]);

  if (!card) return null;

  const decide = (decision) => {
    runInAction(() => {
      if (decision === "granted" && sticky) {
        controller.controlPermissionRemembered = true;
      }
      controller.pendingPermissionCard = null;
      // Spec § 4.4: leave a breadcrumb in scrollback so the user can see
      // what they chose when they look back later.
      controller.messages.push({ role: "system", text: BREADCRUMB[decision] });
    });
    card.resolve(decision);
  };

  return (
    <div
      ref={rootRef}
      className="copilot-permission-card"
      role="dialog"
      aria-label="Permission request"
    >
      <div className="copilot-permission-card__title">
        <MousePointerClick className="tw-h-4 tw-w-4" aria-hidden="true" />
        <span>Allow co-pilot to control your screen?</span>
      </div>
      <div className="copilot-permission-card__body">
        Your co-pilot wants to: <em>{card.taskHint}</em>. You can stop it anytime.
      </div>
      <label className="copilot-permission-card__sticky">
        <input
          type="checkbox"
          checked={sticky}
          onChange={(e) => setSticky(e.target.checked)}
        />
        <span>Don't ask again this session</span>
      </label>
      <div className="copilot-permission-card__buttons">
        <button
          type="button"
          className="copilot-permission-card__btn"
          onClick={() => decide("denied")}
        >
          Just guide me
        </button>
        <button
          type="button"
          className="copilot-permission-card__btn copilot-permission-card__btn--primary"
          onClick={() => decide("granted")}
        >
          Allow
        </button>
      </div>
    </div>
  );
});
